import { DragDropModule } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { EncField, EncSeries, Encoding, EncodingResult } from '@mode-vegas/vegas';
import { IconComponent, TooltipDirective } from '@mode/capra';
import { DragFieldInfo, SelectedChartService } from '@mode/charts/contract';
import { EncodingHelpers } from '@mode/charts/util-vegas';
import { ChartTypes } from '@mode/shared/contract-common';
import { DndModule } from 'ngx-drag-drop';

@Component({
  standalone: true,
  imports: [CommonModule, IconComponent, DragDropModule, DndModule, TooltipDirective],
  selector: 'mode-encoding-field-measure-names',
  templateUrl: './encoding-field-measure-names.component.html',
  styleUrls: ['./encoding-field-measure-names.component.scss'],
})
export class EncodingFieldMeasureNamesComponent implements OnChanges {
  @Input({ required: true }) field!: EncField;
  @Input({ required: true }) encoding!: Encoding;
  @Input({ required: true }) chartService!: SelectedChartService;
  @Input({ required: true }) chartType!: ChartTypes.ChartType;
  @Input() series: EncSeries | undefined;
  @Input() allowContinuous = false;

  @Input() dragInfo: DragFieldInfo | undefined;

  @Output() fieldDropped = new EventEmitter<DragFieldInfo>();

  isDragging = true;
  disabled = false;
  isLinePlusBar = false;
  tooltipContent = '';
  isHorizontalChartType = false;

  readonly LinePlusBarTooltip = 'A single color will be assigned to each measure.<br>Measure Names is Mode-generated';
  readonly VerticalDisabledWarning = 'Remove a field from the Y-axis to add a different field to color';
  readonly HorizontalDisabledWarning = 'Remove a field from the X-axis to add a different field to color';
  readonly GenericTooltip = 'A Mode-generated discrete field that includes data from all measures';

  ngOnChanges(): void {
    this.isLinePlusBar = this.chartType === 'linePlusBar';
    this.isHorizontalChartType = EncodingHelpers.HorizontalChartTypes.has(this.chartType);
    this.isDragging = this.dragInfo?.formula != null;
    this.disabled = this.isLinePlusBar ? true : this.encoding.value.length > 1;
    this.tooltipContent = this.generateTooltip();
  }

  onFieldDrop() {
    const dragInfo = this.dragInfo;
    if (dragInfo?.formula == null) {
      return;
    }
    // Can't replace for linePlusBar. Shouldn't get here since dnd is disabled.
    if (this.isLinePlusBar) {
      return;
    }

    // Can't replace if there are multiple responses
    if (this.encoding.value.length > 1) {
      return;
    }

    if (this.field.id == null) {
      return;
    }

    const formula = dragInfo.formula;

    let newEncoding: EncodingResult | undefined;
    const series = this.series || undefined;
    const zone =
      !this.allowContinuous || formula.isDiscrete
        ? EncodingHelpers.ZoneType.Discrete
        : EncodingHelpers.ZoneType.Aggregate;

    if (formula?.isField && dragInfo?.source && formula.alias) {
      newEncoding = EncodingHelpers.replaceQCMeasureNamesField(this.encoding, formula, 'color', zone);
    } else if (dragInfo?.formula) {
      newEncoding = EncodingHelpers.replaceAndAddNewVegasField(
        this.encoding,
        dragInfo.formula,
        this.field.id,
        'color',
        series ? series.field : this.encoding.marks.series.length ? undefined : '*',
        zone
      );
    }

    if (newEncoding != null) {
      this.chartService.dispatchEncodingUpdate(newEncoding);
    }

    this.fieldDropped.emit();
  }

  generateTooltip() {
    if (this.isLinePlusBar) {
      return this.LinePlusBarTooltip;
    }
    if (this.disabled) {
      return this.isHorizontalChartType ? this.HorizontalDisabledWarning : this.VerticalDisabledWarning;
    }
    return this.GenericTooltip;
  }
}
